<template>
    <div ref="banner" class="bannerCategory" :data-id="banner.id" :data-title="banner.title">
        <a :href="urlBannerRedirect(banner.url)" class="bannerCategory__images" :aria-label="banner.title">
            <div v-if="banner.mediaType === 'video' || banner.image.endsWith('.mp4')" class="bannerCategory__video-container">
                <video class="bannerCategory__video" autoplay muted loop playsinline>
                    <source :src="banner.image" type="video/mp4" />
                </video>
            </div>
            <img v-else :src="banner.image" :alt="banner.title" class="bannerCategory__img" loading="lazy" />
        </a>
        <div class="bannerCategory__bottom">
            <div>
                <h2 class="bannerCategory__title">{{ banner.title }}</h2>
                <p class="bannerCategory__text">{{ banner.description }}</p>
            </div>
            <div class="bannerCategory__col2">
                <a :href="urlBannerRedirect(banner.url)" class="btn btn-primary bannerCategory__btn">{{ banner.cta }}</a>
            </div>
        </div>
        <WarningMessageInBanner
            v-if="banner.warningMessage?.length"
            :message="banner.warningMessage"
            :modal-id="`${banner.id}–warningMessage`"
        />
    </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import BannerTopCategory from '~/models/BannerTopCategory'
import WarningMessageInBanner from '~/components/elements/WarningMessageInBanner.vue'
import { useMatomoViewportTrack, useMatomoClickTrack } from '~/composables/useMatomo'

export default {
    components: { WarningMessageInBanner },
    props: {
        banner: {
            type: Object as PropType<BannerTopCategory>,
            required: true,
        },
    },

    mounted() {
        const element = this.$refs.banner
        if (element) {
            useMatomoViewportTrack(element, {
                category: 'Banner Top Category',
                action: 'view',
                name: element.dataset.title,
                value: +(element.dataset.id ?? 0),
            })

            useMatomoClickTrack(element, {
                category: 'Banner Top Category',
                action: 'click',
                name: element.dataset.title,
                value: +(element.dataset.id ?? 0),
            })
        }
    },

    methods: {
        urlBannerRedirect(url: string): string {
            if (url === '') {
                return ''
            }

            const urlObject = new URL(url)

            return `${urlObject.pathname}${urlObject.search}`
        },
    },
}
</script>

<style lang="scss" scoped>
/*
Structure:
    .bannerCategory
        .bannerCategory__images
            .bannerCategory__img
        .bannerCategory__bottom
            .bannerCategory__col1
                .bannerCategory__title
                .bannerCategory__text
            .bannerCategory__col2
*/

.bannerCategory {
    position: relative;
    box-shadow: $box-shadow;
    border-radius: 10px;
    margin-bottom: 10px;
    overflow: hidden;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.bannerCategory__images {
    aspect-ratio: 16 / 9;
    width: 100%;
    height: 100%;
}

.bannerCategory__img {
    aspect-ratio: 16 / 9;
}

.bannerCategory__video-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
}

.bannerCategory__video {
    max-width: 100%;
    aspect-ratio: 16 / 9;
    //position: absolute;
    //top: 50%;
    //left: 50%;
    //transform: translate(-50%, -50%);
    //width: 100%;
    //min-height: 100%;
    //object-fit: cover;
}

.bannerCategory__bottom {
    padding: 18px 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 10px;
    background-color: $color-white;

    @media (min-width: $max-width-container-inside) {
        flex-direction: row;
    }
}

.bannerCategory__title {
    font-weight: 600;
    font-size: 18px;
    line-height: 23px;
    margin: 0 0 11px 0;
}

.bannerCategory__text {
    font-size: 14px;
    line-height: 19px;
    margin: 11px 0 0 0;
}

.bannerCategory__col2 {
    @media (min-width: $max-width-container-inside) {
        display: flex;
        align-items: center;
    }
}

.bannerCategory__btn {
    padding: 4px 20px;
    min-width: 135px;
}
</style>
